import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2, ArrowRight, Sparkles, Store, ShoppingBag, Landmark, Briefcase, Factory, TrendingUp, CheckCircle2 } from "lucide-react";
import client from "@/api/client";
import { useSiteContent } from "@/context/SiteContentContext";

const ICON_MAP = {
  store: Store,
  "shopping-bag": ShoppingBag,
  landmark: Landmark,
  briefcase: Briefcase,
  factory: Factory,
  "trending-up": TrendingUp,
  sparkles: Sparkles,
};

/**
 * JourneyHero — compact, animated hero letting a visitor pick their
 * business type and preview matching program playbooks. Optimised for
 * density: the tab strip and detail card share the same viewport row
 * on desktop, and template chips render as small pills instead of
 * card blocks. Background art is decorative-only (SVG grid + slow
 * animated orbs) and stays subordinate to the copy.
 */
function stepsLabel(count, isAr) {
  if (!isAr) return count === 1 ? "step" : "steps";
  const n = Math.abs(count);
  if (n === 1) return "خطوة";
  if (n === 2) return "خطوتان";
  if (n >= 3 && n <= 10) return "خطوات";
  return "خطوة";
}

const FADE_UP = {
  hidden: { opacity: 0, y: 10 },
  show: (delay = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] } }),
};

function BackgroundArt() {
  return (
    <>
      {/* SVG dot grid — quiet enough to sit behind copy without stealing focus */}
      <svg
        aria-hidden
        className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.10]"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <pattern id="jh-dots" width="26" height="26" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="1" fill="currentColor" />
          </pattern>
          <radialGradient id="jh-fade" cx="50%" cy="45%" r="65%">
            <stop offset="0%" stopColor="#fff" stopOpacity="1" />
            <stop offset="100%" stopColor="#fff" stopOpacity="0" />
          </radialGradient>
          <mask id="jh-mask">
            <rect width="100%" height="100%" fill="url(#jh-fade)" />
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="url(#jh-dots)" mask="url(#jh-mask)" />
      </svg>
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -top-24 -left-16 h-56 w-56 rounded-full bg-primary/15 blur-3xl"
        animate={{ x: [0, 18, -6, 0], y: [0, 12, -6, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -bottom-28 -right-16 h-64 w-64 rounded-full bg-secondary/25 blur-3xl"
        animate={{ x: [0, -15, 6, 0], y: [0, -10, 8, 0] }}
        transition={{ duration: 24, repeat: Infinity, ease: "easeInOut" }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-primary/[0.08] via-transparent to-transparent pointer-events-none" />
    </>
  );
}

export function JourneyHero() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isAr = i18n.language === "ar";
  const { content } = useSiteContent();
  const jh = content?.journey_hero || {};
  const pick = (en, ar, fallback) => {
    const val = isAr ? ar : en;
    return (val && String(val).trim()) || fallback;
  };
  const eyebrowText = pick(jh.eyebrow_en, jh.eyebrow_ar, isAr ? "ابدأ رحلتك" : "Start your journey");
  const titleText = pick(jh.title_en, jh.title_ar, isAr ? "أخبرنا عن مشروعك — وسنبني لك خارطة الطريق" : "Tell us your business — we'll build the roadmap");
  const subtitleText = pick(jh.subtitle_en, jh.subtitle_ar, isAr ? "اختر نشاطك، عاين البرامج، وانضم بالبريد الإلكتروني." : "Pick your business, preview the playbooks, sign up with just email.");
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSlug, setActiveSlug] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await client.get("/business-types");
        const items = res.data?.items || [];
        setTypes(items);
        if (items.length && !activeSlug) setActiveSlug(items[0].slug);
      } catch (_) { /* silent fail */ }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const active = useMemo(() => types.find((t) => t.slug === activeSlug), [types, activeSlug]);
  const previewTemplates = (active?.templates || []).slice(0, 5);

  return (
    <section className="relative overflow-hidden isolate w-full" data-testid="journey-hero">
      <BackgroundArt />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 relative">
        <div className="text-center max-w-4xl mx-auto">
          <motion.span
            variants={FADE_UP}
            initial="hidden"
            animate="show"
            custom={0}
            className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-xs sm:text-sm uppercase tracking-widest text-primary font-semibold shadow-[0_1px_0_rgba(255,255,255,0.35)_inset]"
          >
            <Sparkles className="h-3.5 w-3.5 animate-pulse" />
            {eyebrowText}
          </motion.span>
          <motion.h1
            variants={FADE_UP} initial="hidden" animate="show" custom={0.06}
            className="font-display text-3xl sm:text-4xl lg:text-6xl font-semibold tracking-tight mt-4 px-2 leading-[1.1]"
          >
            {titleText}
          </motion.h1>
          <motion.p
            variants={FADE_UP} initial="hidden" animate="show" custom={0.12}
            className="text-sm sm:text-base lg:text-lg text-muted-foreground mt-4 px-2 max-w-2xl mx-auto leading-relaxed"
          >
            {subtitleText}
          </motion.p>
        </div>

        {loading ? (
          <div className="flex justify-center mt-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : (
          <div className="mt-6 max-w-5xl mx-auto grid gap-4 lg:grid-cols-[220px_1fr] items-start">
            {/* Vertical vibrant tab rail — compact on desktop, wraps on mobile */}
            <motion.div
              variants={FADE_UP} initial="hidden" animate="show" custom={0.18}
              className="flex lg:flex-col flex-wrap gap-1.5 justify-center lg:justify-start"
              data-testid="journey-hero-tabs"
            >
              {types.map((typ, i) => {
                const Icon = ICON_MAP[typ.icon] || Sparkles;
                const label = isAr ? (typ.name_ar || typ.name_en) : typ.name_en;
                const isActive = typ.slug === activeSlug;
                return (
                  <motion.button
                    key={typ.slug}
                    onClick={() => setActiveSlug(typ.slug)}
                    whileHover={{ y: -1 }}
                    whileTap={{ scale: 0.97 }}
                    initial={{ opacity: 0, x: isAr ? 8 : -8 }}
                    animate={{ opacity: 1, x: 0, transition: { delay: 0.22 + i * 0.03 } }}
                    className={`group relative inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-xs sm:text-[13px] font-medium overflow-hidden transition-[background-color,border-color,color,box-shadow] duration-200 lg:w-full ${
                      isActive
                        ? "border-primary/60 bg-gradient-to-br from-primary via-primary to-primary/85 text-primary-foreground shadow-[0_6px_18px_rgba(45,84,94,0.30),inset_0_1px_0_rgba(255,255,255,0.30)]"
                        : "border-border/70 bg-background/60 backdrop-blur hover:border-primary/40 hover:bg-primary/5"
                    }`}
                    data-testid={`journey-hero-tab-${typ.slug}`}
                  >
                    {/* subtle sweep on active tab */}
                    {isActive && (
                      <span aria-hidden className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent animate-[shimmer_2.6s_ease-in-out_infinite]" />
                    )}
                    <Icon className="h-3.5 w-3.5 relative z-10" />
                    <span className="relative z-10 truncate">{label}</span>
                  </motion.button>
                );
              })}
            </motion.div>

            {/* Detail card — compact, animated cross-fade */}
            <AnimatePresence mode="wait">
              {active && (
                <motion.div
                  key={active.slug}
                  initial={{ opacity: 0, y: 14, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.98 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Card
                    className="relative overflow-hidden rounded-2xl border border-border/60 bg-background/85 backdrop-blur p-4 sm:p-5 shadow-[0_14px_40px_-18px_rgba(18,52,59,0.35)]"
                    data-testid={`journey-hero-detail-${active.slug}`}
                  >
                    <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-primary/8 to-transparent" aria-hidden />

                    <div className="relative flex items-center gap-3 flex-wrap sm:flex-nowrap">
                      <div className="min-w-0 flex-1 min-w-full sm:min-w-0">
                        <h2 className="font-display text-base sm:text-lg font-semibold truncate">
                          {isAr ? (active.name_ar || active.name_en) : active.name_en}
                        </h2>
                        <p className="text-[11px] sm:text-xs text-muted-foreground mt-0.5 line-clamp-2">
                          {isAr ? (active.tagline_ar || active.tagline_en) : active.tagline_en}
                        </p>
                      </div>
                      <GlossyCta onClick={() => navigate(`/journey?type=${active.slug}`)} isAr={isAr} testId={`journey-hero-continue-${active.slug}`}>
                        {t("journeyHero.continue", { defaultValue: isAr ? "ابدأ رحلتي" : "Start my journey" })}
                      </GlossyCta>
                    </div>

                    {previewTemplates.length > 0 && (
                      <motion.ul
                        className="relative mt-3 flex flex-wrap gap-1.5"
                        initial="hidden" animate="show"
                        variants={{ show: { transition: { staggerChildren: 0.05, delayChildren: 0.1 } } }}
                      >
                        {previewTemplates.map((tpl) => (
                          <motion.li
                            key={tpl.template_id}
                            variants={{ hidden: { opacity: 0, y: 6 }, show: { opacity: 1, y: 0 } }}
                            whileHover={{ y: -1 }}
                            className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-gradient-to-br from-muted/40 to-background px-2.5 py-1 text-[11px] transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-[0_4px_14px_rgba(45,84,94,0.10)] max-w-full"
                          >
                            <CheckCircle2 className="h-3 w-3 text-primary flex-shrink-0" />
                            <span className="font-medium truncate max-w-[220px]">{isAr ? (tpl.name_ar || tpl.name) : tpl.name}</span>
                            <span className="font-mono text-muted-foreground text-[10px] whitespace-nowrap">· {tpl.milestone_count}</span>
                          </motion.li>
                        ))}
                        {(active.templates?.length || 0) > previewTemplates.length && (
                          <li className="inline-flex items-center rounded-full border border-dashed border-border/60 px-2.5 py-1 text-[11px] text-muted-foreground">
                            +{active.templates.length - previewTemplates.length} {isAr ? "المزيد" : "more"}
                          </li>
                        )}
                      </motion.ul>
                    )}
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    </section>
  );
}

// ----------------------------------------------------------------------------
// GlossyCta — animated glossy button with looping shine + hover boost.
// Enlarged (h-12, px-6, text-base) for higher tap-target confidence.
// ----------------------------------------------------------------------------
function GlossyCta({ onClick, isAr, testId, children }) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={{ y: -1.5 }}
      whileTap={{ scale: 0.98 }}
      className="btn-glossy relative inline-flex items-center gap-2 rounded-2xl px-5 sm:px-7 h-12 sm:h-14 text-sm sm:text-base font-semibold overflow-hidden group w-full sm:w-auto justify-center"
      data-testid={testId}
    >
      <span aria-hidden className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/35 to-transparent animate-[shimmer_2.8s_ease-in-out_infinite]" />
      <span className="relative z-10 flex items-center gap-2">
        {children}
        <ArrowRight className={`h-4 w-4 sm:h-5 sm:w-5 transition-transform duration-200 ${isAr ? "rotate-180 group-hover:-translate-x-0.5" : "group-hover:translate-x-0.5"}`} />
      </span>
    </motion.button>
  );
}

export default JourneyHero;
